import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import { TooltipProvider } from "./components/ui/tooltip";
import PageTitle from "./components/PageTitle";

// Layouts
import SuperAdminLayout from "./components/layout/SuperAdminLayout";
import CompanyLayout from "./components/layout/CompanyLayout";

// Super Admin Pages
import SuperAdminLogin from "./pages/superadmin/Login";
import Companies from "./pages/superadmin/Companies";
import CompanyDetail from "./pages/superadmin/CompanyDetail";
import SystemSettings from "./pages/superadmin/SystemSettings";
import ProfessionalTaxSettings from "./pages/superadmin/ProfessionalTaxSettings";

// Company Pages
import CompanyLogin from "./pages/company/Login";
import Dashboard from "./pages/company/Dashboard";
import Employees from "./pages/company/Employees";
import EmployeeDetail from "./pages/company/EmployeeDetail";
import LeaveManagement from "./pages/company/LeaveManagement";
import Holidays from "./pages/company/Holidays";
import SalaryStructure from "./pages/company/SalaryStructure";
import Payroll from "./pages/company/Payroll";
import PayrollDetail from "./pages/company/PayrollDetail";
import Performance from "./pages/company/Performance";
import Settings from "./pages/company/Settings";
import SalaryAdvances from "./pages/company/SalaryAdvances";
import OnboardingManagement from "./pages/company/OnboardingManagement";
import TaxManagement from "./pages/company/TaxManagement";
import SetupWizard from "./pages/company/SetupWizard";
import Feeds from "./pages/Feeds";
import AttendanceManagement from "./pages/company/AttendanceManagement";
import UsersRoles from "./pages/company/UsersRoles";
import AssetManagement from "./pages/company/AssetManagement";
import AssetDetails from "./pages/company/AssetDetails";
import OrgChart from "./pages/company/OrgChart";
import ResignationManagement from "./pages/company/ResignationManagement";
import FnFManagement from "./pages/company/FnFManagement";
import Onboarding from "./pages/public/Onboarding";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import CompanyPolicies from "./pages/company/CompanyPolicies";

// Employee Self Service Pages
import MyProfile from "./pages/employee/MyProfile";
import MyLeaves from "./pages/employee/MyLeaves";
import TeamLeaves from "./pages/employee/TeamLeaves";
import TeamAttendance from "./pages/employee/TeamAttendance";
import MyHolidays from "./pages/employee/MyHolidays";
import MyPayslips from "./pages/employee/MyPayslips";
import MyGoals from "./pages/employee/MyGoals";
import MyTaxDeclaration from "./pages/employee/MyTaxDeclaration";
import TaxCertificates from "./pages/employee/TaxCertificates";
import EmployeeDirectory from "./pages/employee/EmployeeDirectory";
import MyAttendance from "./pages/employee/MyAttendance";
import MyAssets from "./pages/employee/MyAssets";
import MyResignation from "./pages/employee/MyResignation";

import ForcePasswordChangeWrapper from "./components/ForcePasswordChangeWrapper";
import ErrorBoundary from "./components/ErrorBoundary";

// Letters & Reports
import LettersReports from "./pages/company/LettersReports";
import AuditLogs from "./pages/company/AuditLogs";

// Stationery Pages
import Stationery from "./pages/stationery/Stationery";
import StationeryDesigner from "./pages/stationery/StationeryDesigner";
import StationeryGenerator from "./pages/stationery/StationeryGenerator";

console.log({ v: 2 });
function App() {
    useEffect(() => {
        console.log("App loaded");
    }, []);

    return (
        <ErrorBoundary>
        <AuthProvider>
            <ForcePasswordChangeWrapper>
                <TooltipProvider>
                    <BrowserRouter>
                        <PageTitle />
                        <Routes>
                            {/* Public Routes */}
                            <Route
                                path="/login"
                                element={
                                    <PublicRoute>
                                        <CompanyLogin />
                                    </PublicRoute>
                                }
                            />
                            <Route path="/onboard" element={<Onboarding />} />
                            <Route
                                path="/privacy-policy"
                                element={<PrivacyPolicy />}
                            />
                            <Route
                                path="/company-policies"
                                element={<CompanyPolicies />}
                            />
                            <Route
                                path="/superadmin/login"
                                element={
                                    <PublicRoute>
                                        <SuperAdminLogin />
                                    </PublicRoute>
                                }
                            />

                            <Route
                                path="/setup"
                                element={
                                    <ProtectedRoute
                                        allowedRoles={[
                                            "COMPANY_ADMIN",
                                            "HR_MANAGER",
                                        ]}
                                    >
                                        <SetupWizard />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Super Admin Routes */}
                            <Route
                                element={
                                    <ProtectedRoute
                                        allowedRoles={["SUPER_ADMIN"]}
                                    >
                                        <SuperAdminLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route
                                    path="/superadmin/companies"
                                    element={<Companies />}
                                />
                                <Route
                                    path="/superadmin/company/:id"
                                    element={<CompanyDetail />}
                                />
                                <Route
                                    path="/superadmin/settings"
                                    element={<SystemSettings />}
                                />
                                <Route
                                    path="/superadmin/pt-settings"
                                    element={<ProfessionalTaxSettings />}
                                />
                            </Route>

                            {/* Company Routes (Admin/HR) */}
                            <Route
                                element={
                                    <ProtectedRoute
                                        allowedRoles={[
                                            "COMPANY_ADMIN",
                                            "HR_MANAGER",
                                            "EMPLOYEE",
                                        ]}
                                    >
                                        <CompanyLayout />
                                    </ProtectedRoute>
                                }
                            >
                                {/* Dashboard - All roles */}
                                <Route
                                    path="/dashboard"
                                    element={<Dashboard />}
                                />

                                {/* Admin/HR Only Routes */}
                                <Route
                                    path="/employees"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Employees />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/employees/:id"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <EmployeeDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/leave"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <LeaveManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/holidays"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Holidays />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/attendance"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <AttendanceManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/salary-structure"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <SalaryStructure />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/salary-advances"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <SalaryAdvances />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/payroll"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Payroll />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/payroll/:runId"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <PayrollDetail />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/performance"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Performance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/tax-management"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <TaxManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/users-roles"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <UsersRoles />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/audit-logs"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <AuditLogs />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/assets"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <AssetManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/assets/:id"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <AssetDetails />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/org-chart"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                                "EMPLOYEE",
                                            ]}
                                        >
                                            <OrgChart />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/resignation"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <ResignationManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/fnf"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <FnFManagement />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/letters-reports"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <LettersReports />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/stationery"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <Stationery />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/stationery/designer/:type"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <StationeryDesigner />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/stationery/designer/:type/:id"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <StationeryDesigner />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/stationery/generate/:id"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <StationeryGenerator />
                                        </ProtectedRoute>
                                    }
                                />

                                {/* Employee Self Service Routes */}
                                <Route
                                    path="/my-profile"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyProfile />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-leaves"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyLeaves />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/team-leaves"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <TeamLeaves />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/team-attendance"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <TeamAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-holidays"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyHolidays />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-attendance"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-payslips"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyPayslips />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-goals"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyGoals />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-tax"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyTaxDeclaration />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/tax-certificates"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <TaxCertificates />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-assets"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyAssets />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/my-resignation"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={["EMPLOYEE"]}
                                        >
                                            <MyResignation />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/directory"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                                "EMPLOYEE",
                                            ]}
                                        >
                                            <EmployeeDirectory />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/onboarding"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                            ]}
                                        >
                                            <OnboardingManagement />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/feeds"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                                "EMPLOYEE",
                                            ]}
                                        >
                                            <Feeds />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/policies"
                                    element={
                                        <ProtectedRoute
                                            allowedRoles={[
                                                "COMPANY_ADMIN",
                                                "HR_MANAGER",
                                                "EMPLOYEE",
                                            ]}
                                        >
                                            <CompanyPolicies />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            {/* Default Redirect */}
                            <Route
                                path="/"
                                element={<Navigate to="/login" replace />}
                            />
                            <Route
                                path="*"
                                element={<Navigate to="/login" replace />}
                            />
                        </Routes>
                    </BrowserRouter>
                    <Toaster
                        position="bottom-center"
                        toastOptions={{
                            style: {
                                background: "rgba(255, 255, 255, 0.9)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                boxShadow:
                                    "0 10px 40px 0 rgba(31, 38, 135, 0.06)",
                                color: "#0f172a",
                                borderRadius: "16px",
                                padding: "12px 16px",
                                fontWeight: "500",
                            },
                        }}
                        richColors
                    />
                </TooltipProvider>
            </ForcePasswordChangeWrapper>
        </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;
